import { FC, useState, ReactNode, useRef } from "react";
import { Info } from "lucide-react";
import { isMobile } from "react-device-detect";
import Modal from "./Modal";
import Button from "./Button";

interface InformationPopoverProps {
  title?: string;
  content: string | ReactNode;
  iconSize?: number;
  iconColor?: string;
  position?: "top" | "bottom" | "left" | "right";
}

const InformationPopover: FC<InformationPopoverProps> = ({
  title,
  content,
  iconSize = 20,
  iconColor = "#474A51",
  position = "top",
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = () => {
    if (isMobile) {
      setShowPopover(!showPopover);
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      // Очищаем предыдущий таймер, если он есть
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowPopover(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Добавляем небольшую задержку перед закрытием
      // чтобы дать время курсору переместиться на popover
      timeoutRef.current = setTimeout(() => {
        setShowPopover(false);
      }, 100); // 100ms достаточно для плавного перехода
    }
  };

  const handlePopoverMouseEnter = () => {
    if (!isMobile) {
      // Отменяем закрытие при наведении на popover
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handlePopoverMouseLeave = () => {
    if (!isMobile) {
      handleMouseLeave(); // Используем ту же логику закрытия
    }
  };

  const handleClose = () => {
    setShowPopover(false);
  };

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  return (
    <>
      {/* Icon trigger and popover wrapper */}
      <div
        className="relative inline-block cursor-pointer"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Info size={iconSize} color={iconColor} />

        {/* Popover (desktop and mobile) */}
        {showPopover && (
          <div
            className={`z-50 ${
              isMobile 
                ? "pointer-events-auto fixed left-4 right-4 absolute mt-2" 
                : `pointer-events-auto absolute !w-[470px] ${positionClasses[position]}`
            }`}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          >
            <div
              className="rounded-[20px] border border-white p-[15px]"
              style={{
                background: "#FFFFFF80",
                backdropFilter: "blur(50px)",
                WebkitBackdropFilter: "blur(50px)",
                boxShadow:
                  "0px 2px 20px 10px rgba(0, 0, 0, 0.10), inset 0px 1px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              {title && (
                <div className="font-medium text-[13px] text-[#232323] mb-2">
                  {title}
                </div>
              )}
              <div className="text-[13px] text-[#232323]">{content}</div>

              {/* Mobile apply button */}
              {isMobile && (
                <Button 
                  fullWidth 
                  onClick={handleClose} 
                  style="outline" 
                  className="!text-[#232323] !mt-4 !border-[#232323] !rounded-[12px] !border-[1px] !h-[39px] !text-center !p-0 !text-[14px] !font-semibold"
                >
                  Понятно
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InformationPopover;